import { useCallback, useContext, useMemo } from "react";
import { useConfirmDialog } from "@k8slens/lds";
import { type ButtonProps } from "@k8slens/lds/lib/es/Button/Button";

import { useGetBusinessInvoices } from "src/hooks/useGetBusinessInvoices";
import { useGetBusinessSubscriptions } from "src/hooks/useGetBusinessSubscriptions";
import { useBusinessBillingPageToken } from "src/hooks/useBillingPageToken";
import { useGetBusinessBillingInformation } from "src/hooks/useGetBusinessBillingInfo";
import { useBusinessBillingPageUrl } from "src/hooks/useBillingPageUrl";
import { useBusinessChildren } from "src/hooks/useBusinessChildren";
import { PlanCode, isLensProPlan, isLensTeamPlan } from "src/services/plans";
import { openUrl } from "src/utils/url";
import { checkoutBaseUrl, pricingUrl } from "src/constants";
import { useGetCurrentBusinessPlan } from "src/hooks/useGetCurrentBusinessPlan";
import { useBusinessUsers } from "src/hooks/useBusinessUsers";

import { BusinessContext } from "src/components/Business/Base";
import PageHeader from "src/components/PageHeader/PageHeader";
import PageSection from "src/components/PageSection/PageSection";
import PageAction from "src/components/PageAction/PageAction";

import { Invoices } from "./Invoices";
import { Billing } from "./Billing";
import { BillingSubscriptions } from "./BillingSubscriptions";

export const BusinessBillingDetails = () => {
  const { businessId, business } = useContext(BusinessContext);
  const { invoices, loading } = useGetBusinessInvoices(businessId);
  const { businessSubscriptions } = useGetBusinessSubscriptions(businessId, true);
  const { businessUsers } = useBusinessUsers(businessId);
  const lensProSubscriptions = useMemo(
    () => (businessSubscriptions || []).filter(({ planCode, state }) => isLensProPlan(planCode) && state !== "expired"),
    [businessSubscriptions],
  );
  const { plan, loading: loadingPlan } = useGetCurrentBusinessPlan(businessId);
  const { businessChildren } = useBusinessChildren(businessId);
  const { token } = useBusinessBillingPageToken(businessId);
  const { billingInfo, loading: loadingBilling } = useGetBusinessBillingInformation(businessId);
  const { billingInfoUrl } = useBusinessBillingPageUrl(businessId);

  const isTeamPlan = isLensTeamPlan(plan?.type);
  const showNewSubscriptionButton = Boolean(business?.reseller);
  const allSubscriptionsExpired = (businessSubscriptions || []).every((s) => s.state === "expired");
  const showPurchaseButton = !showNewSubscriptionButton && (allSubscriptionsExpired || isTeamPlan);

  const { ConfirmDialog, confirm } = useConfirmDialog();
  const handleNewSubscriptions = useCallback(async () => {
    const planCode: PlanCode = "pro-yearly";

    if (lensProSubscriptions.length > 0) {
      const confirmed = await confirm(
        "Create New Subscription?",
        <p>
          Your Lens Business ID already has a Lens Pro subscription associated with it.
          {/* eslint-disable-next-line max-len */}
          If you wish to purchase more seats you may{" "}
          <strong>modify the existing subscription to increase the number of seats</strong>. You can read more about
          adding and changing Lens subscriptions{" "}
          <a
            className="primary"
            target="_blank"
            href="https://docs.k8slens.dev/lens-id/lens-business-id/subscriptions/add-subscriptions/"
            rel="noreferrer"
          >
            here
          </a>
          .<p></p>
          Are you sure you want to create new subscription?
        </p>,
        { confirmText: "Create" },
      );

      if (!confirmed) {
        return;
      }
    }

    openUrl(`${checkoutBaseUrl}/subscribe/${planCode}/${businessId}`, true);
  }, [businessId, confirm, lensProSubscriptions.length]);

  const purchaseButtonProps = useMemo(() => {
    const props: Partial<ButtonProps> & Pick<ButtonProps, "label" | "onClick"> = {
      label: "Purchase",
      onClick: (): void => {
        openUrl(`${pricingUrl}?businessId=${businessId}&quantity=${businessUsers.length}&team_plan_upgrade=true`, true);
      },
      textTransform: "capitalize",
      loading: loadingPlan,
    };

    if (Boolean(business?.external)) {
      props.discreet = true;
      props.disabled = true;
      props.onClick = undefined;
      props.title =
        "Cannot purchase a subscription for this account. Please contact your sales representative to reactivate this subscription.";
    }

    return props;
  }, [businessId, businessUsers.length, business?.external, loadingPlan]);

  return (
    <section>
      <PageHeader
        title="Billing details"
        subtitle={
          <>
            Manage your Lens Business ID <strong>{`(${business?.id})`}</strong> billing, subscriptions and payment
            methods here.
          </>
        }
      />
      <PageSection>
        <PageHeader type="sub" title="Payment method" subtitle="Manage payment method for your Lens Subscriptions." />
        <Billing
          billingInfo={billingInfo}
          billingInfoUrl={billingInfoUrl}
          loading={loadingPlan}
          loadingBilling={loadingBilling}
        />
      </PageSection>
      <PageSection>
        <PageHeader type="sub" title="Subscriptions" subtitle="Manage your Lens subscriptions." />
        <BillingSubscriptions
          businessChildren={businessChildren}
          subscriptions={businessSubscriptions}
          billingPageToken={token}
          loading={loading}
        />
        {ConfirmDialog}
        {showNewSubscriptionButton ? (
          <PageAction
            label={"Create New Subscription"}
            buttonProps={{
              discreet: true,
              onClick: () => handleNewSubscriptions(),
            }}
          />
        ) : null}
        {showPurchaseButton ? <PageAction label={purchaseButtonProps.label} buttonProps={purchaseButtonProps} /> : null}
      </PageSection>
      {business?.external ? null : (
        <PageSection>
          <PageHeader
            title="Billing history"
            subtitle="Review you billing history and manage your invoices."
            type="sub"
          />
          <Invoices
            businessChildren={businessChildren}
            billingPageToken={token}
            invoices={invoices}
            loading={loading}
          />
        </PageSection>
      )}
    </section>
  );
};
